<template>
  <div class="camera-permission">
    <div class="permission-card">
      <!-- Icon -->
      <div class="perm-icon-wrap">
        <span class="material-icons perm-icon">videocam_off</span>
      </div>

      <!-- Title & subtitle -->
      <h2 class="perm-title">Sin acceso a la cámara</h2>
      <p class="perm-subtitle">{{ subtitle }}</p>

      <!-- Retry button: hidden when permission is permanently blocked -->
      <button v-if="canRetry" class="perm-retry-btn" @click="$emit('retry')">
        <span class="material-icons">refresh</span>
        Volver a solicitar permiso
      </button>

      <!-- Device-specific tutorial -->
      <div class="perm-tutorial">
        <div class="tutorial-header" @click="tutorialOpen = !tutorialOpen">
          <span class="material-icons tutorial-icon">help_outline</span>
          <span class="tutorial-label">¿Cómo habilitar la cámara?</span>
          <span class="material-icons tutorial-chevron" :class="{ 'rotated': tutorialOpen }">
            expand_more
          </span>
        </div>

        <transition name="accordion">
          <div v-if="tutorialOpen" class="tutorial-body">
            <!-- Device selector pills -->
            <div class="device-pills">
              <button
                class="device-pill"
                :class="{ active: selectedDevice === 'android' }"
                @click="selectedDevice = 'android'"
              >
                <span class="material-icons">android</span>
                Android
              </button>
              <button
                class="device-pill"
                :class="{ active: selectedDevice === 'iphone' }"
                @click="selectedDevice = 'iphone'"
              >
                <span class="material-icons">phone_iphone</span>
                iPhone
              </button>
              <button
                class="device-pill"
                :class="{ active: selectedDevice === 'desktop' }"
                @click="selectedDevice = 'desktop'"
              >
                <span class="material-icons">laptop</span>
                PC / Mac
              </button>
            </div>

            <!-- Steps -->
            <ol class="tutorial-steps">
              <li
                v-for="(step, i) in currentSteps"
                :key="i"
                class="tutorial-step"
              >
                <span class="step-num">{{ i + 1 }}</span>
                <span class="step-text">{{ step }}</span>
              </li>
            </ol>

            <p class="tutorial-note">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">info</span>
              Después de cambiar los permisos,
              <template v-if="canRetry">pulsa <strong>Volver a solicitar permiso</strong> o </template>recarga la página.
            </p>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
const STEPS = {
  android: [
    'En Chrome, toca el icono del candado 🔒 a la izquierda de la dirección web.',
    'Toca "Permisos".',
    'Busca "Cámara" y cámbiala de "Bloqueada" a "Permitir".',
    'Vuelve a esta página y pulsa "Volver a solicitar permiso".'
  ],
  iphone: [
    'Abre la app "Ajustes" de tu iPhone.',
    'Desplázate hacia abajo y toca "Chrome".',
    'Activa el interruptor de "Cámara".',
    'Vuelve a Chrome y recarga la página.'
  ],
  desktop: [
    'Haz clic en el icono de la cámara 📷 o el candado 🔒 en la barra de dirección (a la izquierda de la URL).',
    'En el menú que aparece, cambia "Cámara" a "Permitir" y cierra el menú.',
    'Si no ves el icono, abre una pestaña nueva y ve a chrome://settings/content/camera',
    'En "Bloqueado", busca este sitio, haz clic en la papelera 🗑 para eliminarlo de la lista.',
    'Vuelve a esta página y pulsa "Volver a solicitar permiso".'
  ]
}

function detectDevice() {
  const ua = navigator.userAgent
  if (/android/i.test(ua)) return 'android'
  if (/iphone|ipad|ipod/i.test(ua)) return 'iphone'
  return 'desktop'
}

export default {
  name: 'CameraPermission',

  props: {
    /** The error name from getUserMedia (e.g. 'NotAllowedError') */
    errorName: {
      type: String,
      default: 'NotAllowedError'
    }
  },

  data() {
    return {
      tutorialOpen: true,
      selectedDevice: detectDevice(),
      // 'prompt' | 'granted' | 'denied' | null (API not supported)
      permissionState: null
    }
  },

  mounted() {
    this._watchPermission()
  },

  beforeDestroy() {
    if (this._permStatus) {
      this._permStatus.onchange = null
    }
  },

  computed: {
    /**
     * The retry button only works when the browser will actually show a
     * permission dialog ('prompt') or when the Permissions API is not
     * supported (we can't tell, so show it as a fallback).
     * When state is 'denied', getUserMedia will fail immediately without
     * any dialog — the button is pointless and should be hidden.
     */
    canRetry() {
      return this.permissionState !== 'denied'
    },
    subtitle() {
      switch (this.errorName) {
        case 'NotAllowedError':
          return this.permissionState === 'denied'
            ? 'El acceso a la cámara está bloqueado. Debes habilitarlo manualmente desde la configuración del navegador.'
            : 'El acceso a la cámara fue denegado. Necesitamos tu permiso para continuar.'
        case 'NotFoundError':
          return 'No se encontró ninguna cámara en este dispositivo.'
        case 'NotReadableError':
          return 'La cámara está siendo usada por otra aplicación. Ciérrala e inténtalo de nuevo.'
        case 'OverconstrainedError':
          return 'La cámara seleccionada no soporta la configuración requerida.'
        default:
          return 'No se pudo iniciar la cámara. Verifica los permisos e inténtalo de nuevo.'
      }
    },
    currentSteps() {
      return STEPS[this.selectedDevice] || STEPS.desktop
    }
  },

  methods: {
    async _watchPermission() {
      if (!navigator.permissions) return
      try {
        const status = await navigator.permissions.query({ name: 'camera' })
        this._permStatus = status
        this.permissionState = status.state
        status.onchange = () => {
          this.permissionState = status.state
        }
      } catch {
        // Permissions API not supported or 'camera' not a valid name on this browser
      }
    }
  }
}
</script>

<style scoped>
.camera-permission {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 280px;
  padding: 1rem;
  background: #111;
}

/* ---- Card ---- */
.permission-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
  padding: 1.5rem 1.25rem;
  background: #1c1c1c;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  gap: 0.75rem;
  text-align: center;
}

/* ---- Icon ---- */
.perm-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.25rem;
}

.perm-icon {
  font-size: 32px;
  color: #f87171;
}

/* ---- Text ---- */
.perm-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.perm-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.5;
}

/* ---- Retry button ---- */
.perm-retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.4rem;
  border: none;
  border-radius: 30px;
  background: linear-gradient(135deg, #c7d533, #a8b920);
  color: #111;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 14px rgba(199, 213, 51, 0.3);
  margin-top: 0.25rem;
}
.perm-retry-btn .material-icons { font-size: 18px; }
.perm-retry-btn:hover {
  transform: scale(1.04);
  box-shadow: 0 6px 20px rgba(199, 213, 51, 0.45);
}
.perm-retry-btn:active { transform: scale(0.97); }

/* ---- Tutorial accordion ---- */
.perm-tutorial {
  width: 100%;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  margin-top: 0.5rem;
  text-align: left;
}

.tutorial-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.tutorial-header:hover { background: rgba(255, 255, 255, 0.08); }

.tutorial-icon { font-size: 18px; color: #c7d533; }
.tutorial-label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  color: #eee;
}
.tutorial-chevron {
  font-size: 20px;
  color: #aaa;
  transition: transform 0.25s ease;
}
.tutorial-chevron.rotated { transform: rotate(180deg); }

.tutorial-body {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.25);
}

/* ---- Device pills ---- */
.device-pills {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.device-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.device-pill .material-icons { font-size: 15px; }
.device-pill.active {
  background: #c7d533;
  border-color: #c7d533;
  color: #111;
  font-weight: 700;
}
.device-pill:not(.active):hover {
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}

/* ---- Steps ---- */
.tutorial-steps {
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
.tutorial-step {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
}
.step-num {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #c7d533;
  color: #111;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.step-text {
  font-size: 0.83rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
}

/* ---- Note ---- */
.tutorial-note {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
  line-height: 1.5;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding-top: 0.65rem;
}

/* ---- Accordion transition ---- */
.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.3s ease, opacity 0.25s ease;
  max-height: 600px;
  overflow: hidden;
}
.accordion-enter,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>